import React, { useRef, useState } from 'react'
import o from '../../components/Overlay.module.css'
import bx from '../BatchesPage.module.css'
import styles from './Labels.module.css'
import { ApiError, apiJson } from '../../lib/api'
import { useLayer } from '../../components/layers'
import { PendingButton, inertClass, useSlow, Failed } from '../../components/pending'
import { LIFECYCLE_NOTE, formatLine, supports, type LabelFormat, type LabelSettings, type Lifecycle, type Purpose, type SettingsView } from './model'

// The Label printing cog (§9.9, h 2949-2976). Tenant-wide, in two tiers: what
// the tenant may print at all is Dataphyll's and a tenant admin sees it
// read-only with the reason; the lifecycle, what a new batch starts with and
// the default formats are the admin's. Which formats a subscription has is
// Dataphyll's too: God Mode allocates them (migration 211), and the admin sees
// only those. Each change saves as it is made, carrying the
// version the dialog is showing, so a change made after someone else's is
// refused and the dialog shows theirs. A lifecycle change is prospective only
// and is confirmed before it is saved.

const CAPABILITIES: { key: 'worker_labels' | 'unassigned_items' | 'job_assignment' | 'generic_items'; title: string; note: string }[] = [
  { key: 'worker_labels', title: 'Worker ID labels', note: 'Lets this subscription print DU labels that identify people.' },
  {
    key: 'unassigned_items',
    title: 'Unassigned item labels',
    note: 'Off by default: every item label belongs to a worker. Switch on only where a subscription genuinely prints stock with no default picker.',
  },
  { key: 'job_assignment', title: 'Picking job assignment', note: 'Allows a job to be attached and its name printed.' },
  // h 2966 names it "Generic tenant item labels"; h 498-500 keeps "tenant" out
  // of anything a user reads, so the title drops the word.
  { key: 'generic_items', title: 'Generic item labels', note: 'SYSADMIN only. TenantId is still implicit. There is no Append TenantId choice anywhere.' },
]

const STARTS: { key: 'start_group' | 'start_job' | 'start_variety'; title: string; note: string }[] = [
  { key: 'start_group', title: 'Worker group', note: 'Prints under the name where the format has room.' },
  { key: 'start_job', title: 'Picking job', note: 'Prints the job name. Never encoded in the QR.' },
  { key: 'start_variety', title: 'Variety', note: 'For crews running two crops off one bin stack.' },
]

const NOT_DATAPHYLL = 'Only Dataphyll can change what this subscription may print.'
const NOT_ADMIN = 'Only an admin changes label printing settings.'
const NOT_ALLOCATED = 'Label formats are allocated by Dataphyll.'

function Switch({ on, label, locked, onClick }: { on: boolean; label: string; locked?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      aria-disabled={locked || undefined}
      className={[o.switch, locked ? styles.switchLocked : ''].join(' ')}
      onClick={onClick}
    />
  )
}

export function LabelSettingsModal({
  user,
  tenantName,
  view,
  onChange,
  onClose,
  toast,
}: {
  user: any
  tenantName: string
  view: SettingsView
  onChange: (view: SettingsView) => void
  onClose: () => void
  toast: (message: string) => void
}) {
  const { settings: s, formats, can_edit: canEdit, can_edit_capabilities: dataphyll } = view
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pendingLife, setPendingLife] = useState<Lifecycle | null>(null)
  const panelEl = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: !!pendingLife && !busy, el: panelEl, modal: true, focusFirst: true, busy: !!busy })
  const slow = useSlow(!!busy)

  const put = async (key: string, patch: Record<string, unknown>, done: (v: SettingsView) => string) => {
    if (busy) return
    setBusy(key)
    setError(null)
    try {
      const v = await apiJson<SettingsView>(user, '/api/label-printing/settings', {
        method: 'PUT',
        body: { ...patch, before_updated_at: s.updated_at },
      })
      onChange(v)
      toast(done(v))
      return true
    } catch (e: any) {
      if (e instanceof ApiError && e.body?.code === 'STALE' && e.body.settings) {
        const { settings, formats, can_edit, can_edit_capabilities, role, capabilities } = e.body
        onChange({ settings, formats, can_edit, can_edit_capabilities, role, capabilities })
        setError('Someone else changed these settings since you opened them. They are shown as they are now; make your change again if it still applies.')
      } else {
        setError(e?.message || 'Could not save the label printing settings')
      }
      return false
    } finally {
      setBusy(null)
    }
  }

  const chooseLife = (life: Lifecycle) => {
    if (!canEdit) return toast(NOT_ADMIN)
    setPendingLife(life === s.item_lifecycle ? null : life)
  }
  const saveLife = async () => {
    if (!pendingLife) return
    const ok = await put('lifecycle', { item_lifecycle: pendingLife, confirm_lifecycle: true }, (v) => `New items will be ${v.settings.item_lifecycle}.`)
    if (ok) setPendingLife(null)
  }

  const flipCapability = (key: (typeof CAPABILITIES)[number]['key'], title: string) => {
    if (!dataphyll) return toast(NOT_DATAPHYLL)
    put(key, { [key]: !s[key] }, (v) => `${title} ${v.settings[key] ? 'switched on' : 'switched off'}.`)
  }
  const flipStart = (key: (typeof STARTS)[number]['key'], title: string) => {
    if (!canEdit) return toast(NOT_ADMIN)
    put(key, { [key]: !s[key] }, (v) => `New batches start with ${title} ${v.settings[key] ? 'ticked' : 'unticked'}.`)
  }
  const setIdMode = (mode: LabelSettings['start_id_mode']) => {
    if (!canEdit) return toast(NOT_ADMIN)
    if (mode === s.start_id_mode) return
    put('start_id_mode', { start_id_mode: mode }, () => `New item batches start in ${mode === 'Numeric' ? 'Numeric only' : 'Alphanumeric'}.`)
  }
  const flipFormat = (f: LabelFormat) => {
    if (!dataphyll) return toast(NOT_ALLOCATED)
    put('format:' + f.id, { enabled_formats: { [f.id]: !f.enabled } }, () => `${f.name} ${f.enabled ? 'turned off' : 'enabled'}.`)
  }
  const makeDefault = (f: LabelFormat, purpose: Purpose) => {
    if (!canEdit) return toast(NOT_ADMIN)
    const key = purpose === 'worker' ? 'default_worker_sku' : 'default_item_sku'
    put('default:' + purpose + f.id, { [key]: f.sku }, () => `${f.name} is now the ${purpose === 'worker' ? 'Worker ID' : 'item'} default.`)
  }

  const capabilities = CAPABILITIES.filter((c) => c.key !== 'generic_items' || dataphyll)
  // A subscription's admin sees the formats allocated to it; Dataphyll sees them all, and can switch one here too.
  const shownFormats = dataphyll ? formats : formats.filter((f) => f.enabled)

  return (
    <div className={o.scrim} onClick={requestClose}>
      <div
        ref={panelEl}
        className={[o.modal, o.wide, styles.cog].join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label="Label printing settings"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>Label printing settings</h2>
            <p className={o.sub}>{tenantName ? `${tenantName} · every batch in this subscription` : 'Every batch in this subscription'}</p>
          </div>
          <button type="button" className={o.close} aria-label="Close" disabled={!!busy} onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25">
              <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className={[o.body, inertClass(!!busy, slow)].join(' ')}>
          {!canEdit && <div className={o.note}><div className={o.noteLine}><span style={{ fontSize: '0.7813rem', lineHeight: 1.5, color: 'var(--dp-ink2)' }}>{NOT_ADMIN} You can see what is set, but not change it.</span></div></div>}

          <section className={o.body} style={{ gap: '0.625rem', paddingBottom: '0.875rem', borderBottom: '1px solid var(--dp-line-soft)', marginBottom: '0.875rem' }} aria-label="Item lifecycle">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className={o.label}>Item lifecycle</span>
            </div>
            <div className={styles.seg} role="radiogroup" aria-label="Item lifecycle">
              {(['Single-use', 'Reusable'] as Lifecycle[]).map((life) => {
                const on = (pendingLife || s.item_lifecycle) === life
                return (
                  <button
                    key={life}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    aria-disabled={!canEdit || undefined}
                    className={[styles.segBtn, on ? styles.segOn : ''].join(' ')}
                    onClick={() => chooseLife(life)}
                  >
                    {life}
                  </button>
                )
              })}
            </div>
            <div className={bx.hint}>{LIFECYCLE_NOTE[pendingLife || s.item_lifecycle]}</div>
            {pendingLife && (
              <div className={o.warn} role="alert">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3v18M5 8h14" />
                </svg>
                <div>
                  <div style={{ marginBottom: '0.625rem' }}>
                    Changing this to {pendingLife} affects only items created afterwards. Every existing item keeps the lifecycle it was created with.
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <PendingButton className={o.primary} busy={busy === 'lifecycle'} pending="Saving…" onClick={saveLife} style={{ marginLeft: 0, height: '2rem', padding: '0 1rem' }}>
                      Save the change
                    </PendingButton>
                    <button type="button" className={o.ghost} disabled={!!busy} onClick={() => setPendingLife(null)} style={{ height: '2rem', padding: '0 1rem' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </section>

          <section className={o.body} style={{ gap: '0.625rem', paddingBottom: '0.875rem', borderBottom: '1px solid var(--dp-line-soft)', marginBottom: '0.875rem' }} aria-label="What this subscription can print">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className={o.label}>What this subscription can print</span>
              <span className={styles.badge}>Dataphyll only</span>
            </div>
            <div className={bx.hint}>
              {dataphyll
                ? 'You are Dataphyll. These capabilities decide what the subscription may print at all.'
                : 'Set by Dataphyll. An admin can see what is switched on but cannot change it. Turning a capability on has commercial and traceability consequences.'}
            </div>
            {capabilities.map((c) => (
              <div key={c.key} className={o.toggleRow}>
                <div className={o.toggleText}>
                  <div className={o.toggleTitle}>{c.title}</div>
                  <div className={o.toggleSub}>{c.note}</div>
                </div>
                <Switch on={!!s[c.key]} label={c.title} locked={!dataphyll} onClick={() => flipCapability(c.key, c.title)} />
              </div>
            ))}
          </section>

          <section className={o.body} style={{ gap: '0.625rem', paddingBottom: '0.875rem', borderBottom: '1px solid var(--dp-line-soft)', marginBottom: '0.875rem' }} aria-label="What a new batch starts with">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className={o.label}>What a new batch starts with</span>
              <span className={styles.badgeYours}>Yours to set</span>
            </div>
            <div className={bx.hint}>
              Starting state, not permission. An operator can still change any of these on the batch in front of them. Set them the way you print most of the
              time and nobody has to remember.
            </div>
            {STARTS.map((c) => (
              <div key={c.key} className={o.toggleRow}>
                <div className={o.toggleText}>
                  <div className={o.toggleTitle}>{c.title}</div>
                  <div className={o.toggleSub}>{c.note}</div>
                </div>
                <Switch on={!!s[c.key]} label={c.title} locked={!canEdit} onClick={() => flipStart(c.key, c.title)} />
              </div>
            ))}
            <div className={`${o.toggleRow} ${styles.startRow}`}>
              <div className={o.toggleText}>
                <div className={o.toggleTitle}>Display ID</div>
                <div className={o.toggleSub}>Which namespace item batches start in.</div>
              </div>
              <div className={styles.seg} role="radiogroup" aria-label="Display ID namespace">
                {(['Alphanumeric', 'Numeric'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    role="radio"
                    aria-checked={s.start_id_mode === mode}
                    aria-disabled={!canEdit || undefined}
                    className={[styles.segBtn, s.start_id_mode === mode ? styles.segOn : ''].join(' ')}
                    onClick={() => setIdMode(mode)}
                  >
                    {mode === 'Numeric' ? 'Numeric only' : 'Alphanumeric'}
                  </button>
                ))}
              </div>
            </div>
          </section>

          <section className={o.body} style={{ gap: '0.625rem', paddingBottom: '0.875rem', marginBottom: 0 }} aria-label="Formats and defaults">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className={o.label}>Formats and defaults</span>
              <span className={styles.badgeYours}>Defaults yours to set</span>
            </div>
            <div className={bx.hint}>Dataphyll allocates the formats this subscription prints on; you choose the default for each kind of label.</div>
            {shownFormats.length === 0 && <div className={bx.hint}>No label formats are allocated to this subscription yet.</div>}
            {shownFormats.map((f) => (
              <div key={f.id} className={styles.formatRow} data-testid={`format-${f.sku}`}>
                <label className={styles.formatName}>
                  {dataphyll && <input type="checkbox" checked={f.enabled} aria-label={`${f.name} enabled`} onChange={() => flipFormat(f)} />}
                  <span className={bx.switchText}>
                    <span className={bx.switchTitle}>{f.name}</span>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.4, color: 'var(--dp-muted)', maxWidth: '13.125rem' }}>{formatLine(f)}</span>
                  </span>
                </label>
                <span className={styles.defaults}>
                  {(['worker', 'item'] as Purpose[])
                    .filter((p) => supports(f, p))
                    .map((p) => {
                      const on = p === 'worker' ? f.is_default_worker : f.is_default_item
                      return (
                        <button
                          key={p}
                          type="button"
                          aria-pressed={on}
                          className={[styles.defaultBtn, on ? styles.defaultOn : ''].join(' ')}
                          disabled={!canEdit || !f.enabled || on}
                          title={!f.enabled ? `Enable ${f.name} before making it a default` : undefined}
                          onClick={() => makeDefault(f, p)}
                        >
                          {p === 'worker' ? 'Worker default' : 'Item default'}
                        </button>
                      )
                    })}
                </span>
              </div>
            ))}
          </section>
        </div>
        <div className={o.foot}>
          <span style={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.4, color: 'var(--dp-muted)', maxWidth: '13.125rem' }}>{canEdit ? 'Each change is saved as you make it.' : ''}</span>
          <span className={styles.footSpacer} />
          <Failed text={error} />
          <button type="button" className={o.ghost} disabled={!!busy} onClick={requestClose}>
            Done
          </button>
        </div>
      </div>
      {confirm}
    </div>
  )
}
